/**
 * Appointment status labels and allowed actions.
 * Mirrors backend validate_appointment_status_transition — do not invent transitions.
 */

export const TERMINAL_STATUSES = new Set([
  "completed",
  "cancelled_by_patient",
  "cancelled_by_clinic",
  "no_show",
]);

export const APPOINTMENT_TYPES = [
  "office",
  "home",
  "hospital",
  "nursing_home",
  "teleconsultation",
  "personal_block",
];

const STATUS_LABELS_ES = {
  scheduled: "Programada",
  confirmed: "Confirmada",
  waiting: "En espera",
  in_progress: "En consulta",
  completed: "Completada",
  cancelled_by_patient: "Cancelada (paciente)",
  cancelled_by_clinic: "Cancelada (clínica)",
  no_show: "No asistió",
  rescheduled: "Reprogramada",
};

const STATUS_LABELS_EN = {
  scheduled: "Scheduled",
  confirmed: "Confirmed",
  waiting: "Waiting",
  in_progress: "In progress",
  completed: "Completed",
  cancelled_by_patient: "Cancelled (patient)",
  cancelled_by_clinic: "Cancelled (clinic)",
  no_show: "No-show",
  rescheduled: "Rescheduled",
};

const TYPE_LABELS_ES = {
  office: "Consultorio",
  home: "Domicilio",
  hospital: "Hospital",
  nursing_home: "Geriátrico",
  teleconsultation: "Teleconsulta",
  personal_block: "Bloqueo personal",
};

const TYPE_LABELS_EN = {
  office: "Office",
  home: "Home visit",
  hospital: "Hospital",
  nursing_home: "Nursing home",
  teleconsultation: "Teleconsultation",
  personal_block: "Personal block",
};

/** Visual variants (badge). Status text is always shown — not color-only. */
export const STATUS_VARIANTS = {
  scheduled: "bg-slate-100 text-slate-800 border-slate-200",
  confirmed: "bg-sky-100 text-sky-900 border-sky-200",
  waiting: "bg-amber-100 text-amber-900 border-amber-200",
  in_progress: "bg-indigo-100 text-indigo-900 border-indigo-200",
  completed: "bg-emerald-100 text-emerald-900 border-emerald-200",
  cancelled_by_patient: "bg-rose-50 text-rose-800 border-rose-200",
  cancelled_by_clinic: "bg-rose-50 text-rose-800 border-rose-200",
  no_show: "bg-orange-100 text-orange-900 border-orange-200",
  rescheduled: "bg-violet-100 text-violet-900 border-violet-200",
};

export function getStatusLabel(status, { english = false } = {}) {
  const map = english ? STATUS_LABELS_EN : STATUS_LABELS_ES;
  return map[status] || status || "—";
}

export function getTypeLabel(type, { english = false } = {}) {
  const map = english ? TYPE_LABELS_EN : TYPE_LABELS_ES;
  return map[type] || type || "—";
}

export function isTerminalStatus(status) {
  return TERMINAL_STATUSES.has(String(status || ""));
}

export function isPersonalBlock(type) {
  return String(type || "") === "personal_block";
}

/**
 * Allowed UI actions for an appointment.
 * Action ids: confirm, waiting, start, complete, no_show,
 * cancel_patient, cancel_clinic, reschedule, edit, open_record, start_consultation
 */
export function getAllowedActions(status, appointmentType) {
  const st = String(status || "");
  const type = String(appointmentType || "");
  const personal = type === "personal_block";

  if (isTerminalStatus(st)) {
    return personal ? [] : ["open_record"].filter(Boolean);
  }

  if (personal) {
    if (st === "scheduled") {
      return ["complete", "cancel_clinic", "reschedule", "edit"];
    }
    return [];
  }

  const baseRecord = ["open_record", "start_consultation"];

  if (st === "scheduled") {
    return [
      "confirm",
      "waiting",
      "start",
      "complete",
      "no_show",
      "cancel_patient",
      "cancel_clinic",
      "reschedule",
      "edit",
      ...baseRecord,
    ];
  }
  if (st === "confirmed") {
    return [
      "waiting",
      "start",
      "complete",
      "no_show",
      "cancel_patient",
      "cancel_clinic",
      "reschedule",
      "edit",
      ...baseRecord,
    ];
  }
  if (st === "waiting") {
    return ["start", "complete", "cancel_patient", "cancel_clinic", "edit", ...baseRecord];
  }
  if (st === "in_progress") {
    return ["complete", "cancel_clinic", "edit", ...baseRecord];
  }
  return [...baseRecord];
}

export function actionRequiresCancellationReason(actionId) {
  return actionId === "cancel_patient" || actionId === "cancel_clinic";
}

export function actionToStatusPayload(actionId) {
  switch (actionId) {
    case "confirm":
      return { status: "confirmed" };
    case "waiting":
      return { status: "waiting" };
    case "start":
      return { status: "in_progress" };
    case "complete":
      return { status: "completed" };
    case "no_show":
      return { status: "no_show" };
    case "cancel_patient":
      return { status: "cancelled_by_patient" };
    case "cancel_clinic":
      return { status: "cancelled_by_clinic" };
    case "reschedule":
      return { status: "rescheduled" };
    default:
      return null;
  }
}

export function getActionLabel(actionId, { english = false } = {}) {
  const es = {
    confirm: "Confirmar",
    waiting: "Marcar en espera",
    start: "Iniciar consulta",
    complete: "Completar",
    no_show: "No asistió",
    cancel_patient: "Cancelar (paciente)",
    cancel_clinic: "Cancelar (clínica)",
    reschedule: "Reprogramar",
    edit: "Editar datos",
    open_record: "Abrir expediente",
    start_consultation: "Ir a nueva consulta",
  };
  const en = {
    confirm: "Confirm",
    waiting: "Mark waiting",
    start: "Start visit",
    complete: "Complete",
    no_show: "No-show",
    cancel_patient: "Cancel (patient)",
    cancel_clinic: "Cancel (clinic)",
    reschedule: "Reschedule",
    edit: "Edit details",
    open_record: "Open record",
    start_consultation: "New visit",
  };
  return (english ? en : es)[actionId] || actionId;
}

export function summarizeDayCounts(appointments = []) {
  const list = Array.isArray(appointments) ? appointments : [];
  return {
    total: list.length,
    scheduled: list.filter((a) => a.status === "scheduled").length,
    confirmed: list.filter((a) => a.status === "confirmed").length,
    completed: list.filter((a) => a.status === "completed").length,
    cancelledOrNoShow: list.filter((a) =>
      ["cancelled_by_patient", "cancelled_by_clinic", "no_show"].includes(a.status)
    ).length,
  };
}
